import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ChangeATMPINStackParamList, ChangeATMPINStackRoutes } from './type';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface ChangeATMPINProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  account: string;
  tab?: number;
  record?: Card;
  func?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
}

export const ChangeATMPINNativeOneScreen: React.FC<ChangeATMPINProps> = (props) => {
  const { onClose, onLayout, data, account, tab, record, func, onReturnData } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, textAtmPin, btnView, btn, modal, topHeader } = sxStyles;
  const [loading, setLoading] = useState(false);
  const showPassword = (atmPin: string) => {
    return `${atmPin.substring(0, 1)}****${atmPin.substring(atmPin.length - 1)}`;
  };

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<ChangeATMPINStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');

  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        index={1}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View />}>
        <View sx={modal}>
          <View sx={{ minHeight: 280, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <View sx={topHeader}>
              <BottomSheetHeader
                title={t('card-account-management::change-atm-pin.title--change-atm-pin-confirmation')}
                onPress={() => navigation.popToTop()}
              />
            </View>
            <View sx={body}>
              <TextBody sx={textMb} semiBold>
                {t('card-account-management::change-atm-pin.text--change-atm-pin-inquiry')}
              </TextBody>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account-management::change-atm-pin.text--new-atm-pin')}
              </TextCaption>
              <H6 sx={textAtmPin}>{showPassword(data.pin)}</H6>
              {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={btnView}>
                <UniButton
                  loading={loading}
                  sx={btn}
                  labelSx={{ color: '$balck' }}
                  label={t('card-account-management::general.button--place-instruction')}
                  onPress={() => {
                    if (func) {
                      setLoading(true);
                      func(
                        record?.id!,
                        data.pin,
                        (returnData) => {
                          setLoading(false);
                          onReturnData && onReturnData(returnData.data);
                          navigation.navigate(ChangeATMPINStackRoutes.Placed, {
                            onLayout: onLayout,
                            data,
                            account,
                            tab,
                            returnData: returnData.data
                          });
                        },
                        (error) => {
                          setLoading(false);
                          setAlertMessage(error?.message);
                        }
                      );
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {},
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$15'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
